/* eslint-disable complexity */
import { DataPoint, PriceChangesChart, TsPoint } from '@toggle/design-system';
import {
  Entity,
  entityTagToCountry,
  formatTicker,
  PriceHistory,
} from '@toggle/toggle';
import clsx from 'clsx';
import { format, getDate, getMonth, getYear, isAfter } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FlagIcon } from '~/components/flag-icon/FlagIcon';
import { InsightsLivePriceChange } from '~/components/insights-live-price-change/InsightsLivePriceChange';
import { NEW_YORK_TIMEZONE } from '~/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';

import { MarketReactionLivePrice } from '../market-reaction-live-price/MarketReactionLivePrice';
import styles from './MarketReactionCharts.module.scss';

export interface LineData {
  data: { time: string; value?: number }[];
  lastProportionChange?: number;
  prevCloseData?: PriceHistory[number];
  lastKnownPrice?: number;
  lastKnownDate?: string;
}

export interface MarketReactionChartProps {
  fromDateEST?: Date;
  toDateEST?: Date;
  lines: LineData[];
  entities?: Entity[];
}

export const MarketReactionChart = ({
  fromDateEST,
  toDateEST,
  lines,
  entities,
}: MarketReactionChartProps) => {
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );
  const { t } = useTranslation(['insights']);
  const { colors } = useChartTheme(['soft']);
  const fillColors = {
    positive: colors.threshold.neutral.value,
    negative: colors.threshold.negative.value,
  };

  if (!lines.length || !entities || entities.length === 0 || !toDateEST) {
    return null;
  }

  const getVerticalLines = () => {
    if (!fromDateEST || !toDateEST) {
      return [];
    }

    return [
      {
        x: fromDateEST,
      },
      {
        x: new Date(
          getYear(fromDateEST),
          getMonth(fromDateEST),
          getDate(fromDateEST),
          16,
          0,
          0,
          0
        ),
        isDashed: true,
      },
      {
        x: new Date(
          getYear(toDateEST),
          getMonth(toDateEST),
          getDate(toDateEST),
          9,
          30,
          0,
          0
        ),
      },
      {
        x: toDateEST,
        isDashed: true,
      },
    ];
  };

  const nowEST = toZonedTime(new Date(), NEW_YORK_TIMEZONE);
  const isWindowComplete = isAfter(nowEST, toDateEST);

  const multiColors = colors.getMulti(lines.length);
  const chartLines = lines.map((line, i) => {
    const data: DataPoint[] = line.data.map(item => ({
      date: toZonedTime(new Date(item.time), NEW_YORK_TIMEZONE),
      value: item.value,
    }));
    const entity = entities[i];
    return {
      data,
      label: entity ? formatTicker(entity) : '',
      color:
        lines.length > 1 ? multiColors[i].value : colors.line.default.value,
    };
  });

  const isMultipleEntities = !!entities && entities.length > 1;

  const mergedValues = lines
    .flatMap(line => line.data.map(item => item.value))
    .filter((value): value is number => value !== undefined);

  const range =
    mergedValues.length > 0
      ? Math.max(...mergedValues) - Math.min(...mergedValues)
      : 0;
  const yAxisFormatType = isMultipleEntities
    ? range > 4
      ? 'percent'
      : 'percent1f'
    : 'default';
  const formattedEndDate = format(toDateEST, 'MMM d');
  const thresholdData =
    chartLines.length === 1
      ? chartLines[0].data.filter(
          (item): item is TsPoint => item.value !== undefined
        )
      : undefined;

  const firstLine = lines[0];
  return (
    <div className={styles.container}>
      <div className={styles.chartTitleWrapper}>
        <div className={styles.chartTitle}>
          <span className={clsx(styles.title)}>
            {t('insights:catalyst.drawer.chartTitle')}
          </span>
          <div
            className={clsx(styles.windowDescription, {
              [styles.windowDescriptionV2]: isUniversalDrawerEnabled,
            })}
          >
            <svg
              width={12}
              height={12}
              viewBox="0 0 40 40"
              fill={
                isWindowComplete ? 'var(--icon-soft)' : 'var(--icon-success)'
              }
            >
              <circle cx="20" cy="20" r="12" />
            </svg>
            <span className={styles.titleText}>
              {isWindowComplete
                ? t('insights:catalyst.drawer.windowComplete')
                : t('insights:catalyst.drawer.liveWindow')}
            </span>
            <span className={styles.titleText}>
              {isWindowComplete
                ? t('insights:catalyst.drawer.finalAsOfDateClose', {
                    date: formattedEndDate,
                  })
                : t('insights:catalyst.drawer.endsAtClose', {
                    date: formattedEndDate,
                  })}
            </span>
          </div>
        </div>
        {isMultipleEntities ? (
          firstLine.prevCloseData ? (
            <span className={styles.dateSpan}>
              {t('analyze:analysis.sinceDateClose', {
                date: formatInTimeZone(
                  firstLine.prevCloseData.time,
                  'UTC',
                  'MMM d'
                ),
              })}
            </span>
          ) : null
        ) : (
          <MarketReactionLivePrice
            entity={entities[0]}
            isWindowComplete={isWindowComplete}
            eventProportionChange={firstLine.lastProportionChange}
            lastKnownPrice={firstLine.lastKnownPrice}
            lastKnownDate={firstLine.lastKnownDate}
            prevCloseData={firstLine.prevCloseData}
          />
        )}
      </div>
      {isMultipleEntities && (
        <div className={styles.multipleLegendWrapper}>
          {entities.map((entity, index) => {
            const currentLine = lines[index];
            return (
              <div className={styles.multipleLegend} key={entity.tag}>
                <div className={styles.etfLegendItem}>
                  <div className={styles.iconWrapper}>
                    <div
                      className={styles.legendItemVector}
                      style={{ borderColor: multiColors[index].value }}
                    />
                    <FlagIcon
                      countryCode={entityTagToCountry[entity.tag]}
                      variant="circular"
                      customSize={16}
                    />
                  </div>
                  <div className={styles.etfDetails}>
                    <span className={styles.etfName}>{entity.name_short}</span>
                    <span className={styles.etfTag}>
                      {formatTicker(entity)}
                    </span>
                  </div>
                </div>
                <InsightsLivePriceChange
                  entity={entity}
                  proportionChange={currentLine.lastProportionChange}
                  isWindowComplete={isWindowComplete}
                />
              </div>
            );
          })}
        </div>
      )}
      {!!lines.length && (
        <PriceChangesChart
          lines={chartLines}
          hasTooltip
          tooltipFormatType={isMultipleEntities ? 'percent2f' : 'default2f'}
          fillColors={fillColors}
          yAxisFormatType={yAxisFormatType}
          thresholdData={thresholdData}
          verticalLines={getVerticalLines()}
          tooltipSubtitle={
            isMultipleEntities && firstLine.prevCloseData
              ? t('analyze:analysis.sinceDateClose', {
                  date: format(firstLine.prevCloseData.time, 'MMM d'),
                })
              : undefined
          }
        />
      )}
    </div>
  );
};
